
/**
 * Centralized error handling utility
 * Provides consistent error logging and handling across the application
 */
import { ERROR_TYPES } from './constants';

/**
 * Error types enum
 */
export const ErrorType = {
  DATA_LOAD: 'DATA_LOAD',
  DATA_SAVE: 'DATA_SAVE',
  VALIDATION: 'VALIDATION',
  NETWORK: 'NETWORK',
  AUTH: 'AUTH',
  PERMISSION: 'PERMISSION',
  VERSION: 'VERSION',
  CACHE: 'CACHE',
  STORAGE: 'STORAGE',
  ANALYSIS: 'ANALYSIS',
  GENERAL: 'GENERAL'
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL'
};

/**
 * Standard error message format
 * @param {string} component - Component or service name
 * @param {string} method - Method name
 * @param {string} message - Error message
 * @param {object} context - Additional context
 * @returns {string} Formatted error message
 */
const formatErrorMessage = (component, method, message, context = {}) => {
  const timestamp = new Date().toISOString();
  const contextStr = Object.keys(context).length > 0
    ? ` | Context: ${JSON.stringify(context)}`
    : '';

  return `[${timestamp}] [${component}] ${method} - ${message}${contextStr}`;
};

/**
 * Log error with consistent formatting
 * @param {string} component - Component or service name
 * @param {string} method - Method name
 * @param {string} message - Error message
 * @param {Error|any} error - Error object
 * @param {ErrorType} type - Error type
 * @param {ErrorSeverity} severity - Error severity
 * @param {object} context - Additional context
 */
export const logError = (
  component,
  method,
  message,
  error = null,
  type = ErrorType.GENERAL,
  severity = ErrorSeverity.ERROR,
  context = {}
) => {
  const formattedMessage = formatErrorMessage(component, method, message, context);

  // Add error details if available
  const errorDetails = error ? {
    errorMessage: error.message || String(error),
    errorStack: error.stack,
    errorType: type,
    severity
  } : { errorType: type, severity };

  // Log based on severity
  switch (severity) {
    case ErrorSeverity.INFO:
      // eslint-disable-next-line no-console
      console.log(formattedMessage, errorDetails);
      break;
    case ErrorSeverity.WARNING:
      // eslint-disable-next-line no-console
      console.warn(formattedMessage, errorDetails);
      break;
    case ErrorSeverity.ERROR:
    case ErrorSeverity.CRITICAL:
      // eslint-disable-next-line no-console
      console.error(formattedMessage, errorDetails);
      break;
    default:
      // eslint-disable-next-line no-console
      console.error(formattedMessage, errorDetails);
  }

  // For critical errors, could add additional handling like sending to error tracking service
  if (severity === ErrorSeverity.CRITICAL) {
    // Note: hook for error tracking service (e.g., Sentry) if/when configured.
  }
};

/**
 * Handle async errors consistently
 * @param {Function} asyncFunction - Async function to execute
 * @param {string} component - Component name
 * @param {string} method - Method name
 * @param {object} options - Options for error handling
 * @returns {Promise<any>} Result or default value on error
 */
export const handleAsync = async (
  asyncFunction,
  component,
  method,
  options = {}
) => {
  const {
    defaultValue = null,
    errorType = ErrorType.GENERAL,
    severity = ErrorSeverity.ERROR,
    context = {},
    rethrow = false
  } = options;

  try {
    return await asyncFunction();
  } catch (error) {
    logError(
      component,
      method,
      'Async operation failed',
      error,
      errorType,
      severity,
      context
    );

    if (rethrow) {
      throw error;
    }

    return defaultValue;
  }
};

/**
 * Create a component-specific error logger
 * @param {string} component - Component name
 * @returns {object} Component-specific logging methods
 */
export const createErrorLogger = (component) => {
  return {
    info: (method, message, context = {}) =>
      logError(component, method, message, null, ErrorType.GENERAL, ErrorSeverity.INFO, context),

    warn: (method, message, context = {}) =>
      logError(component, method, message, null, ErrorType.GENERAL, ErrorSeverity.WARNING, context),

    error: (method, message, error = null, type = ErrorType.GENERAL, context = {}) =>
      logError(component, method, message, error, type, ErrorSeverity.ERROR, context),

    critical: (method, message, error = null, type = ErrorType.GENERAL, context = {}) =>
      logError(component, method, message, error, type, ErrorSeverity.CRITICAL, context),

    async: (asyncFunction, method, options = {}) =>
      handleAsync(asyncFunction, component, method, options)
  };
};

/**
 * User-friendly error messages
 */
export const UserMessages = {
  [ErrorType.DATA_LOAD]: 'Failed to load data. Please try again.',
  [ErrorType.DATA_SAVE]: 'Failed to save data. Please try again.',
  [ErrorType.VALIDATION]: 'Invalid data provided. Please check your input.',
  [ErrorType.NETWORK]: 'Network error. Please check your connection.',
  [ErrorType.AUTH]: 'Authentication failed. Please log in again.',
  [ErrorType.PERMISSION]: 'You do not have permission to perform this action.',
  [ErrorType.VERSION]: 'Version operation failed. Please try again.',
  [ErrorType.CACHE]: 'Cache operation failed.',
  [ErrorType.STORAGE]: 'Storage operation failed.',
  [ErrorType.ANALYSIS]: 'Analysis operation failed. Please try again.',
  [ErrorType.GENERAL]: 'An unexpected error occurred. Please try again.'
};

/**
 * Get user-friendly error message
 * @param {ErrorType} errorType - Type of error
 * @param {string} customMessage - Optional custom message
 * @returns {string} User-friendly error message
 */
export const getUserMessage = (errorType, customMessage = null) => {
  return customMessage || UserMessages[errorType] || UserMessages[ErrorType.GENERAL];
};

/**
 * Categorize error types
 * @param {Error} error - The error to categorize
 * @returns {string} Error type from ERROR_TYPES
 */
export function categorizeError(error) {
  if (!error) return ERROR_TYPES.UNKNOWN;

  const message = error.message?.toLowerCase() || '';
  const code = error.code?.toLowerCase() || '';

  // Network errors
  if (code === 'err_network' || message.includes('network') || message.includes('fetch')) {
    return ERROR_TYPES.NETWORK;
  }

  // Authentication errors
  if (error.status === 401 || message.includes('unauthorized') || message.includes('authentication')) {
    return ERROR_TYPES.AUTHENTICATION;
  }

  // Authorization errors
  if (error.status === 403 || message.includes('forbidden') || message.includes('permission')) {
    return ERROR_TYPES.AUTHORIZATION;
  }

  // Not found errors
  if (error.status === 404 || message.includes('not found')) {
    return ERROR_TYPES.NOT_FOUND;
  }

  // Validation errors
  if (error.status === 400 || message.includes('validation') || message.includes('invalid')) {
    return ERROR_TYPES.VALIDATION;
  }

  // Server errors
  if (error.status >= 500 || message.includes('server')) {
    return ERROR_TYPES.SERVER;
  }

  return ERROR_TYPES.UNKNOWN;
}

/**
 * Format error message for user display
 * @param {Error} error - The error to format
 * @param {string} context - Context where error occurred
 * @returns {string} User-friendly error message
 */
export function formatUserMessage(error, context = '') {
  const errorType = categorizeError(error);
  const contextPrefix = context ? `${context}: ` : '';

  switch (errorType) {
    case ERROR_TYPES.NETWORK:
      return `${contextPrefix}Network error. Please check your connection and try again.`;

    case ERROR_TYPES.AUTHENTICATION:
      return `${contextPrefix}Authentication failed. Please log in again.`;

    case ERROR_TYPES.AUTHORIZATION:
      return `${contextPrefix}You don't have permission to perform this action.`;

    case ERROR_TYPES.NOT_FOUND:
      return `${contextPrefix}The requested resource was not found.`;

    case ERROR_TYPES.VALIDATION:
      return `${contextPrefix}Please check your input and try again.`;

    case ERROR_TYPES.SERVER:
      return `${contextPrefix}Server error. Please try again later.`;

    default:
      return `${contextPrefix}An unexpected error occurred. Please try again.`;
  }
}

/**
 * Retry an operation with exponential backoff
 * @param {Function} operation - The operation to retry
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retries
 * @param {number} options.initialDelay - Initial delay in milliseconds
 * @param {number} options.maxDelay - Maximum delay in milliseconds
 * @param {Function} options.shouldRetry - Function to determine if should retry
 * @returns {Promise<any>} Result of the operation
 */
export async function retryWithBackoff(operation, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = () => true
  } = options;

  let lastError;
  const logger = createErrorLogger('retryWithBackoff');

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries || !shouldRetry(error, attempt)) {
        throw error;
      }

      const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);
      logger.warn(
        `Attempt ${attempt + 1} failed, retrying in ${delay}ms`,
        { error: error.message }
      );

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Create a timeout wrapper for promises
 * @param {Promise} promise - The promise to wrap
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} timeoutMessage - Custom timeout message
 * @returns {Promise} The wrapped promise
 */
export function withTimeout(promise, timeoutMs, timeoutMessage = 'Operation timed out') {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
    )
  ]);
}

/**
 * Batch error reporter for collecting and reporting multiple errors
 */
export class ErrorBatcher {
  constructor(options = {}) {
    this.errors = [];
    this.component = options.component || 'ErrorBatcher';
    this.flushInterval = options.flushInterval || 5000;
    this.maxBatchSize = options.maxBatchSize || 10;
    this.timer = null;
    this.logger = createErrorLogger(this.component);
  }

  add(error, context = '') {
    this.errors.push({ error, context, timestamp: new Date() });

    if (this.errors.length >= this.maxBatchSize) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), this.flushInterval);
    }
  }

  flush() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (this.errors.length === 0) return;

    const errorBatch = [...this.errors];
    this.errors = [];

    this.logger.error('Error batch', {
      count: errorBatch.length,
      errors: errorBatch
    });
  }

  clear() {
    this.errors = [];
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

// Export a singleton error batcher for the application
export const globalErrorBatcher = new ErrorBatcher({
  component: 'GlobalErrorBatcher',
  flushInterval: 10000,
  maxBatchSize: 20
});

// Create default error handler instance
const errorHandler = {
  logError,
  handleAsync,
  createErrorLogger,
  getUserMessage,
  categorizeError,
  formatUserMessage,
  ErrorType,
  ErrorSeverity,
  retryWithBackoff,
  withTimeout,
  ErrorBatcher,
  globalErrorBatcher
};

// Export default error handler instance
export default errorHandler;
